import React, { useState, ReactNode } from "react";
import { Menu, TextInput, Text, Box, Highlight, Avatar, Flex, Stack } from "@mantine/core";
import { IconSearch } from "@tabler/icons";
import { getInitials } from "../../utils/getInitials";
import { CLASS_NAMES } from "./bagel-autocomplete-constants";

interface Item {
  _id?: string;
  id?: string;
  useId?: boolean;
  name: string;
  childComponents?: Item[];
  email?: string;
  picture?: string;
}

interface BagelAutocompleteProps {
  items: Item[];
  selected: any;
  children: ReactNode;
  onSelect: (item: Item) => void;
  disabled?: boolean;
  searchAccessor?: string;
  withinPortal?: boolean;
  trapFocus?: boolean;
  zIndex?: number;
}

const BagelAutocomplete: React.FC<BagelAutocompleteProps> = ({
  items,
  selected,
  children,
  onSelect,
  disabled,
  searchAccessor = "name",
  withinPortal = true,
  trapFocus = true,
  zIndex,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Function to recursively filter items and child items
  const filterItems = (items: Item[], term: string): Item[] => {
    term = term.toLowerCase();
    if (!items || items.length === 0) return [];
    return items.filter((item: any) =>
      item[searchAccessor]?.toLowerCase().includes(term) ||
      item.email?.toLowerCase().includes(term) ||
      (item.childComponents && filterItems(item.childComponents, term).length > 0)
    );
  };

  const filteredItems = filterItems(items, searchTerm);

  const renderMenuItems = (items: Item[]) => {
    return items.map((item, index) => {
      const hasAvatar = item.picture !== undefined || item.email !== undefined;
      
      return (
        <React.Fragment key={item.id || item._id || item.name || index}>
          <Menu.Item
            className={CLASS_NAMES.optionItem}
            py={hasAvatar ? 8 : 5}
            onClick={() => onSelect(item)}
            sx={{
              backgroundColor:
                (item.useId ? item.id : item.name) === selected
                  ? "#F5F6FE"
                  : "transparent",
            }}
          >
            {hasAvatar ? (
              <Flex align="center" gap={8}>
                <Avatar src={item.picture} radius="xl" size={28}>
                  {item.name ? getInitials(item.name) : ''}
                </Avatar>
                <Stack spacing={0}>
                  <Text size="sm" className={CLASS_NAMES.optionName}>
                    <Highlight highlight={searchTerm}>{item.name}</Highlight>
                  </Text>
                  {item.email && (
                    <Text size="xs" c="dimmed">
                      <Highlight highlight={searchTerm}>{item.email}</Highlight>
                    </Text>
                  )}
                </Stack>
              </Flex>
            ) : (
              <Highlight highlight={searchTerm}>{item.name}</Highlight>
            )}
          </Menu.Item>
          {item.childComponents && item.childComponents.length > 0 && (
            <Box pl="md">
              {renderMenuItems(item.childComponents)}
            </Box>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Menu
      shadow="md"
      withinPortal={withinPortal}
      trapFocus={trapFocus}
      zIndex={zIndex}
    >
      <Menu.Target>{children}</Menu.Target>
      {!disabled && (
        <Menu.Dropdown maw={280}>
          <TextInput
            icon={<IconSearch size={14} />}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
          <Box
            sx={{
              overflowY: "scroll",
            }}
            mah={400}
            pt="xs"
          >
            {renderMenuItems(filteredItems)}
            {filteredItems.length === 0 && (
              <Text p="sm" color="dimmed" align="center" size="xs">
                No matches for "{searchTerm}"
              </Text>
            )}
          </Box>
        </Menu.Dropdown>
      )}
    </Menu>
  );
};

export default BagelAutocomplete;